/**
 * Historique des actions Chantier
 * Trace toutes les actions : ajout documents, envoi signature, modification date, etc.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
    History, FileText, FileSignature, Calendar, Upload, 
    Edit, ChevronDown, ChevronUp, CheckCircle2, Send, Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const ACTION_ICONS = {
    etude_ajoutee: Upload,
    audit_ajoute: Upload,
    subvention_ajoutee: Upload,
    devis_ajoute: Upload,
    devis_selectionne: CheckCircle2,
    signature_envoyee: Send,
    signature_consultee: Eye,
    signature_signee: FileSignature,
    signature_refusee: FileSignature,
    date_chantier_modifiee: Calendar,
    planning_modifie: Edit,
};

const ACTION_LABELS = {
    etude_ajoutee: 'Étude PDF ajoutée',
    audit_ajoute: 'Audit ajouté',
    subvention_ajoutee: 'Capture subvention ajoutée',
    devis_ajoute: 'Devis ajouté',
    devis_selectionne: 'Devis sélectionné comme actif',
    signature_envoyee: 'Signature électronique envoyée',
    signature_consultee: 'Document consulté par le client',
    signature_signee: 'Document signé par le client',
    signature_refusee: 'Signature refusée',
    date_chantier_modifiee: 'Date de chantier modifiée',
    planning_modifie: 'Planning chantier modifié',
};

const ACTION_COLORS = {
    etude_ajoutee: 'bg-blue-100 text-blue-700',
    audit_ajoute: 'bg-purple-100 text-purple-700',
    subvention_ajoutee: 'bg-indigo-100 text-indigo-700',
    devis_ajoute: 'bg-amber-100 text-amber-700',
    devis_selectionne: 'bg-green-100 text-green-700',
    signature_envoyee: 'bg-cyan-100 text-cyan-700',
    signature_consultee: 'bg-teal-100 text-teal-700',
    signature_signee: 'bg-emerald-100 text-emerald-700',
    signature_refusee: 'bg-red-100 text-red-700',
    date_chantier_modifiee: 'bg-orange-100 text-orange-700',
    planning_modifie: 'bg-slate-100 text-slate-700',
};

function HistoryItem({ entry }) {
    const Icon = ACTION_ICONS[entry.action] || FileText;
    const label = ACTION_LABELS[entry.action] || entry.action;
    const colorClass = ACTION_COLORS[entry.action] || 'bg-slate-100 text-slate-700';
    
    return (
        <div className="flex items-start gap-3 py-3 border-b last:border-b-0">
            <div className={cn("p-2 rounded-lg", colorClass)}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">{label}</p>
                {entry.details && (
                    <p className="text-xs text-slate-500 mt-0.5">{entry.details}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-slate-400">
                        {format(new Date(entry.timestamp), 'dd MMM yyyy à HH:mm', { locale: fr })}
                    </span>
                    {entry.user_name && (
                        <>
                            <span className="text-xs text-slate-300">•</span>
                            <span className="text-xs text-slate-500">par {entry.user_name}</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export function ChantierHistory({ history = [], maxVisible = 5 }) {
    const [expanded, setExpanded] = useState(false);
    
    if (!history || history.length === 0) {
        return (
            <Card data-testid="chantier-history">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Historique des actions
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-slate-400 text-center py-4">
                        Aucune action enregistrée
                    </p>
                </CardContent>
            </Card>
        );
    }
    
    const sortedHistory = [...history].sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
    );
    
    const visibleHistory = expanded ? sortedHistory : sortedHistory.slice(0, maxVisible);
    const hasMore = sortedHistory.length > maxVisible;

    return (
        <Card data-testid="chantier-history">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Historique des actions
                    </CardTitle>
                    <Badge variant="secondary">{history.length} action(s)</Badge>
                </div>
            </CardHeader>
            <CardContent>
                <div className="divide-y">
                    {visibleHistory.map((entry, idx) => (
                        <HistoryItem key={entry.id || idx} entry={entry} />
                    ))}
                </div>
                
                {hasMore && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? (
                            <>
                                <ChevronUp className="w-4 h-4 mr-2" />
                                Voir moins
                            </>
                        ) : (
                            <>
                                <ChevronDown className="w-4 h-4 mr-2" />
                                Voir tout ({sortedHistory.length - maxVisible} de plus)
                            </>
                        )}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}

export default ChantierHistory;
